import { isReadOnlyViewer } from '../../utils/hackathonStatus';
import { ideaAllowance } from '../../services/billing.service';
import React, { useMemo } from 'react';

import { HackathonPayload } from '../../services/hackathon.service';
import { IdeaCard } from './IdeaCard';
import { NewIdea } from './NewIdea';
import { useAuth } from '../../services/useAuth';
import Stack from '@mui/material/Stack/Stack';
import Alert from '@mui/material/Alert/Alert';
import Button from '@mui/material/Button/Button';
import { Link } from 'react-router-dom';
import ToggleButton from '@mui/material/ToggleButton/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup/ToggleButtonGroup';
import Chip from '@mui/material/Chip/Chip';
import Paper from '@mui/material/Paper/Paper';
import Typography from '@mui/material/Typography/Typography';
import LightbulbOutlinedIcon from '@mui/icons-material/LightbulbOutlined';
import Card from '@mui/material/Card/Card';
import CardContent from '@mui/material/CardContent/CardContent';
import Box from '@mui/material/Box/Box';
import Snackbar from '@mui/material/Snackbar/Snackbar';
import { IdeaPayload } from '../../services/idea.service';
import List from '@mui/material/List/List';
import ListItemButton from '@mui/material/ListItemButton/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import { useDesktopMode } from '../../services/hooks/useDesktopMode';
import { currentTeamOf, formatDate, ideasOpen, resultsAnnounced, teamJoinBlockedReason, teamsLockedReason } from '../../utils/hackathonStatus';
import ListItem from '@mui/material/ListItem/ListItem';
export const IdeasList = ({
    hackathon,
    reloadHackathon
}: {
    hackathon: HackathonPayload,
    reloadHackathon: () => void
}) => {

    const desktopMode = useDesktopMode();
    const { user } = useAuth();
    const [hideFullTeams, setHideFullTeams] = React.useState(false);
    const [sortBy, setSortBy] = React.useState<'newest' | 'likes'>('likes');
    const newIdeaElement = React.useRef<HTMLElement | null>(null);

    const [showNotification, setShowNotification] = React.useState(false);
    const [notificationMessage, setNotificationMessage] = React.useState('');

    const isInTeam = useMemo(() => {
        return !!user && hackathon.ideas.some(idea => !idea.archived && idea.members.some(m => m.email === user.email));
    }, [hackathon.ideas, user])

    const joinBlockedReason = useMemo(() => teamJoinBlockedReason(hackathon, user ? user.email : undefined), [hackathon, user])
    const myTeam = useMemo(() => currentTeamOf(hackathon, user ? user.email : undefined), [hackathon, user])

    const isOrganizer = useMemo(() => {
        return !!user && hackathon.organizers.some(o => o.email === user.email);
    }, [user, hackathon.organizers])

    const isJudge = useMemo(() => {
        return !!user && hackathon.judges.some(j => j.email === user.email);
    }, [user, hackathon.judges])

    // judges score the ideas rather than pitch or join them (an organizer who also judges still can)
    const judgeOnly = isJudge && !isOrganizer;
    const acceptingIdeas = ideasOpen(hackathon);
    // closing submissions (or the deadline) holds back participants; organizers can still add ideas until results
    const canSubmitIdea = !judgeOnly && (acceptingIdeas || (isOrganizer && !resultsAnnounced(hackathon)));
    // the order holds while the page is open, so a live refresh (a like, someone leaving) can't move a card under the cursor;
    // changing the sort or reloading re-sorts. New ideas go at the end, or at the top when they're your own.
    const order = React.useRef<{ sortBy: string, ids: string[] }>({ sortBy: '', ids: [] });
    const list = useMemo(() => {
        const sorted = hackathon.ideas.filter(i => (isOrganizer || (!isOrganizer && !i.archived))).sort((a, b) => {
            // current team is always at top
            if (!!user) {
                if (a.members.some(m => m.email === user.email))
                    return -1;
                if (b.members.some(m => m.email === user.email))
                    return 1;
            }
            switch (sortBy) {
                case 'likes':
                    return b.likedBy.length - a.likedBy.length;
                case 'newest':
                    return new Date(b.submittedAt).getTime() - new Date(a.submittedAt).getTime();
                default:
                    return 0;
            }
        });
        const byId = new Map(sorted.map(idea => [idea._id, idea]));
        let ids = sorted.map(idea => idea._id);
        if (order.current.sortBy === sortBy) {
            const kept = order.current.ids.filter(id => byId.has(id));
            const added = sorted.filter(idea => !kept.includes(idea._id));
            const mine = (idea: IdeaPayload) => !!user && idea.members.some(m => m.email === user.email);
            ids = [...added.filter(mine).map(i => i._id), ...kept, ...added.filter(i => !mine(i)).map(i => i._id)];
        }
        order.current = { sortBy, ids };
        return ids.map(id => byId.get(id)!).filter(idea => {
            if (hideFullTeams) {
                return idea.members.length < hackathon.maxTeamSize;
            }
            return true;
        });
    }, [hackathon, hackathon.ideas, user, hideFullTeams, sortBy, isOrganizer])

    const elementRef = React.useRef<HTMLDivElement | null>(null);
    const leftSpace = elementRef.current?.getBoundingClientRect().left ?? 0;

    return (
        <Stack ref={elementRef} direction="column" spacing={4}>
            {
                list.length > 2 && leftSpace > 310 && <Paper variant="outlined" sx={{
                    position: 'fixed',
                    top: 88,
                    left: 16,
                    zIndex: 100,
                    padding: 1,
                    width: Math.min(leftSpace - 40, 280),
                    maxHeight: 'calc(100vh - 120px)',
                    overflow: 'auto',
                    borderRadius: '14px'
                }}>
                    <Typography variant="overline" color="text.secondary" sx={{ px: 2 }}>Jump to idea</Typography>
                    <List>
                        {list.map((idea: IdeaPayload) => {
                            return (
                                <ListItemButton key={idea._id} sx={{ borderRadius: '8px' }} onClick={() => {
                                    const el = document.getElementById(idea._id);
                                    if (el) {
                                        el.scrollIntoView({ behavior: 'smooth' });
                                    }
                                }}>
                                    <ListItemText primary={idea.title} primaryTypographyProps={{ variant: 'body2', noWrap: true }} />
                                </ListItemButton>
                            )
                        })}
                        {
                            canSubmitIdea && <ListItem>
                                <Button variant={'contained'} color={'primary'} onClick={() => {
                                    newIdeaElement.current?.scrollIntoView({ behavior: 'smooth' });
                                }}>
                                    Submit an idea
                                </Button>
                            </ListItem>
                        }


                    </List>
                </Paper>
            }
            <Stack direction="column" spacing={2}>
                {judgeOnly && (<Alert severity="info" action={
                    <Button color="inherit" size="small" component={Link} to={`/h/${hackathon.slug}/judge`}>Open Judging</Button>
                }>
                    {resultsAnnounced(hackathon)
                        ? <>You judged this hackathon. The results are in; the final scores are on the Judging page.</>
                        : hackathon.votingPeriodOn
                            ? <>You're judging this hackathon. Voting is open: score each team on the Judging page.</>
                            : <>You're judging this hackathon. Read the ideas below; you'll score each team on the Judging page once voting opens.</>}
                </Alert>)}
                {list.length > 0 && !isInTeam && !isOrganizer && !isJudge && (<Alert severity="info">
                    {joinBlockedReason
                        ? `You're not on a team. ${joinBlockedReason}.`
                        : `You're not on a team yet. Pick an idea below and click "Join team". Pitching an idea doesn't put you on its team, so join yours too if you'll work on it.`}
                </Alert>)}
                <Stack spacing={2} direction={{ xs: 'column', sm: 'row' }} alignItems={{ xs: 'flex-start', sm: 'center' }} justifyContent={'space-between'}>
                    <Stack direction="row" spacing={1.5} alignItems="center">
                        <Typography variant="h3" component="h2" id="ideas" sx={{ scrollMarginTop: 88 }}>Ideas</Typography>
                        {list.length > 0 && <Chip size="small" label={list.length} />}
                        {
                            canSubmitIdea && <Button size="small" variant="outlined" onClick={() => {
                                newIdeaElement.current?.scrollIntoView({ behavior: 'smooth' });
                                window.setTimeout(() => document.querySelector<HTMLInputElement>('#new-idea-form input')?.focus({ preventScroll: true }), 400);
                            }}>Pitch an idea</Button>
                        }
                    </Stack>
                    <ToggleButtonGroup
                        exclusive
                        size="small"
                        aria-label="Sort ideas"
                        value={hideFullTeams ? 'open' : sortBy}
                        onChange={(_, value) => {
                            if (!value) {
                                return;
                            }
                            if (value === 'open') {
                                setHideFullTeams(true);
                                setSortBy('likes');
                            } else {
                                setHideFullTeams(false);
                                setSortBy(value);
                            }
                        }}
                        sx={{
                            backgroundColor: 'action.hover',
                            borderRadius: 999,
                            p: 0.5,
                            gap: 0.5,
                            width: { xs: '100%', sm: 'auto' },
                            '& .MuiToggleButton-root': {
                                flex: 1,
                                border: 'none',
                                borderRadius: '999px !important',
                                px: 2,
                                minHeight: 36,
                                textTransform: 'none',
                                fontWeight: 600,
                                color: 'text.secondary',
                                whiteSpace: 'nowrap'
                            },
                            '& .Mui-selected': {
                                backgroundColor: 'background.paper !important',
                                color: 'text.primary',
                                boxShadow: '0 1px 2px rgba(15,23,42,0.08)'
                            }
                        }}>
                        <ToggleButton value="likes">Most liked</ToggleButton>
                        <ToggleButton value="newest">Newest</ToggleButton>
                        <ToggleButton value="open">Open spots</ToggleButton>
                    </ToggleButtonGroup>
                </Stack>
                {
                    list.length === 0 && (
                        <Card sx={{ borderStyle: 'dashed' }}>
                            <CardContent sx={{ py: 5, textAlign: 'center' }}>
                                <LightbulbOutlinedIcon color="action" sx={{ fontSize: 40, mx: 'auto' }} />
                                <Typography variant="h5" sx={{ mt: 1 }}>No ideas yet</Typography>
                                <Typography color="text.secondary">Be the first to pitch one below.</Typography>
                            </CardContent>
                        </Card>
                    )
                }
                {
                    list.map((idea: IdeaPayload) => {
                        return (
                            <Card key={idea._id} id={idea._id} sx={{ scrollMarginTop: 88 }}>
                                <CardContent sx={{ p: { xs: 2, md: 3 } }}>
                                    <IdeaCard
                                        isOrganizer={isOrganizer}
                                        hackathonId={hackathon.slug}
                                        ideaId={idea._id}
                                        joinBlockedReason={joinBlockedReason}
                                        currentTeam={myTeam}
                                        showJoin={!judgeOnly}
                                        editLocked={hackathon.winners.length > 0}
                                        teamsLocked={!!teamsLockedReason(hackathon)}
                                        readOnly={isReadOnlyViewer(hackathon, user ? user.email : undefined)}
                                        maxTeamSize={hackathon.maxTeamSize}
                                        onUnarchive={() => {
                                            reloadHackathon();
                                        }}
                                        onDelete={() => {
                                            reloadHackathon();
                                        }}
                                        onReloadIdea={() => {
                                            reloadHackathon();
                                        }}
                                    />

                                </CardContent>
                            </Card>
                        )
                    })
                }
            </Stack>

            <Box ref={newIdeaElement}>
                <Stack direction="column" spacing={2}>
                    {
                        !acceptingIdeas && (
                            <Alert severity="info">
                                {hackathon.winners.length > 0
                                    ? 'Results have been announced, so new ideas are closed.'
                                    : hackathon.ideasClosedAt
                                        ? `The organizers closed idea submissions on ${formatDate(hackathon.ideasClosedAt, 'ddd, MMM D [at] h:mm A')}.`
                                        : `Idea submission closed at the end of ${formatDate(hackathon.ideaSubmissionDeadline, 'ddd, MMM D')}.`}
                                {canSubmitIdea && ' As an organizer you can still add ideas, for yourself or on behalf of a participant.'}
                            </Alert>
                        )
                    }
                    {
                        canSubmitIdea && (
                            <Card><CardContent sx={{ p: { xs: 2, md: 3 } }}>
                            {/* the form confirms each idea itself and stays put, so several can be pitched in a row */}
                            <NewIdea hackathonId={hackathon._id} hackathonSlug={hackathon.slug} template={hackathon.ideaTemplate}
                                allowance={ideaAllowance(hackathon)}
                                upgradeUrl={isOrganizer ? `/h/${hackathon.slug}/upgrade` : undefined}
                                onBehalfOptions={isOrganizer ? hackathon.participants.filter(p => !user || p.email !== user.email) : undefined}
                                onCreate={() => {
                                reloadHackathon();
                            }} />
                            </CardContent></Card>
                        )
                    }
                </Stack>


            </Box>
            <Snackbar
                open={showNotification}
                autoHideDuration={6000}
                onClose={() => {
                    setShowNotification(false);
                }}
                message={notificationMessage}
                color={notificationMessage.includes('error') ? 'error' : 'success'}
            />
        </Stack>
    )

}